import { useState, useEffect, memo } from 'react';
import { Card, Skeleton, useSpinner } from '@chikara/ui';
import type { LinkedAccount } from '../../types/credits.types';
import styles from './LinkedAccountSection.module.css';
import { t } from '@/i18n';

export interface LinkedAccountSectionProps {
  data: LinkedAccount;
  supportEmail?: string;
}

// --- Sub-components ---

const LinkedAccountSkeleton = memo(({ isMobile }: { isMobile: boolean }) => (
  <div className={styles.wrapper}>
    {!isMobile ? (
      <Card className={styles.accountCard}>
        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', padding: '1rem', gap: '2rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Skeleton width="100px" height="14px" />
            <Skeleton width="160px" height="24px" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Skeleton width="100px" height="14px" />
            <Skeleton width="180px" height="24px" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Skeleton width="100px" height="14px" />
            <Skeleton width="140px" height="24px" />
          </div>
        </div>
      </Card>
    ) : (
      <Card className={styles.mainCard}>
        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Skeleton width="150px" height="18px" />
          <Skeleton width="100%" height="50px" />
          <Skeleton width="100%" height="50px" />
        </div>
      </Card>
    )}
  </div>
));

const LinkedAccountMobile = memo(({ 
  data, 
  isExpanded, 
  supportEmail,
  onToggle 
}: { data: LinkedAccount; isExpanded: boolean; supportEmail?: string; onToggle: () => void }) => (
  <div className={styles.wrapper}>
    <Card className={styles.mainCard}>
      <div className={styles.cardHeader}>
        <span className={styles.cardHeaderTitle}>{t('credits.linkedAccountTitle')}</span>
        <button 
          type="button" 
          className={styles.headerToggleButton}
          onClick={onToggle}
        >
          {isExpanded ? t('common.viewLess') : t('common.viewMore')}
        </button>
      </div>

      {isExpanded && (
        <>
          <div className={styles.cardDetails}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t('credits.accountLabel')}</span>
              <span className={styles.detailValue}>{data.accountName}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t('credits.clabeLabel')}</span>
              <span className={styles.detailValue}>{data.clabe}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t('credits.bankLabel')}</span>
              <span className={styles.detailValue}>{data.bankName}</span>
            </div>
          </div>
          <p className={styles.helpText}>
            {t('credits.supportLink')}{' '}
            <a
              href={`mailto:${supportEmail}`}
              className={styles.supportLink}
              rel="noopener noreferrer"
            >
              {supportEmail}
            </a>
          </p>
        </>
      )}
    </Card>
  </div>
));

const LinkedAccountDesktop = memo(({ 
  data, 
  supportEmail 
}: LinkedAccountSectionProps) => (
  <section className={styles.section} aria-labelledby="linked-account-title">
    <h2 id="linked-account-title" className={styles.sectionTitle}>
      {t('credits.linkedAccountTitle')}
    </h2>
    <Card className={styles.accountCard}>
      <Card.Stats
        label={t('credits.accountLabel')}
        value={data.accountName}
        className={styles.statItem}
      />
      <Card.Stats
        label={t('credits.clabeLabel')}
        value={data.clabe}
        className={styles.statItem}
      />
      <Card.Stats
        label={t('credits.bankLabel')}
        value={data.bankName}
        className={styles.statItem}
      />
    </Card>
    <p className={styles.helpText}>
      {t('credits.linkedAccountHelp')}{' '}
      <a
        href={`mailto:${supportEmail}`}
        className={styles.supportLink}
        rel="noopener noreferrer"
      >
        {supportEmail}
      </a>
    </p>
  </section>
));

// --- Main Component ---

export function LinkedAccountSection({
  data,
  supportEmail = 'ayuda@delt.ai',
}: LinkedAccountSectionProps) {
  const { isSpinnerLoading } = useSpinner();
  const [isMobile, setIsMobile] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (isSpinnerLoading) {
    return <LinkedAccountSkeleton isMobile={isMobile} />;
  }

  if (isMobile) {
    return (
      <LinkedAccountMobile 
        data={data} 
        supportEmail={supportEmail}
        isExpanded={isExpanded} 
        onToggle={() => setIsExpanded(!isExpanded)} 
      />
    );
  }

  return <LinkedAccountDesktop data={data} supportEmail={supportEmail} />;
}

LinkedAccountSection.displayName = 'LinkedAccountSection';
LinkedAccountSkeleton.displayName = 'LinkedAccountSkeleton';
LinkedAccountMobile.displayName = 'LinkedAccountMobile';
LinkedAccountDesktop.displayName = 'LinkedAccountDesktop';
